import { useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { useEditContext } from '@/contexts/EditContext'
import { cn } from '@/lib/utils'

interface EditableRichTextProps {
  value: string
  onChange: (html: string) => void
  className?: string
  placeholder?: string
}

export function EditableRichText({
  value,
  onChange,
  className,
  placeholder = 'Cliquez pour editer...',
}: EditableRichTextProps) {
  const { isEditMode, setActiveEditor } = useEditContext()

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        code: false,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      Link.configure({
        openOnClick: !isEditMode,
        HTMLAttributes: {
          class: 'text-primary-600 underline hover:text-primary-700',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    editable: isEditMode,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    onFocus: () => {
      if (editor) {
        setActiveEditor(editor)
      }
    },
    onBlur: () => {
      // Small delay to allow toolbar clicks
      setTimeout(() => {
        setActiveEditor(null)
      }, 200)
    },
    immediatelyRender: false,
  })

  // Update content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  // Update editable state when edit mode changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditMode)
    }
  }, [isEditMode, editor])

  // Register editor on focus
  useEffect(() => {
    if (editor?.isFocused) {
      setActiveEditor(editor)
    }
  }, [editor?.isFocused, editor, setActiveEditor])

  if (!isEditMode) {
    // View mode: render static HTML
    return (
      <div
        className={cn('prose max-w-none', className)}
        dangerouslySetInnerHTML={{ __html: value || `<p class="text-gray-400">${placeholder}</p>` }}
      />
    )
  }

  return (
    <div
      className={cn(
        'prose max-w-none',
        'outline-none transition-all rounded-sm',
        'hover:ring-2 hover:ring-primary-300 hover:ring-offset-2',
        '[&_.ProseMirror:focus]:ring-2 [&_.ProseMirror:focus]:ring-primary-500 [&_.ProseMirror:focus]:ring-offset-2',
        className
      )}
    >
      <EditorContent
        editor={editor}
        className="outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[1em]"
      />
    </div>
  )
}
